import React, { useLayoutEffect, useMemo, useState, useCallback, useEffect, } from 'react';
import { Keyboard, View } from 'react-native';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import { AppView } from 'design-system/AppView';
import Button, { HEIGHT } from 'design-system/Button';
import { DaysOfTheWeek } from 'design-system/day-of-the-week-picker/values';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import WeeklyView from 'features/budgeting/components/WeeklyView';
import FixedDateView from 'features/budgeting/components/FixedDateView';
import EveryMonthView from 'features/budgeting/components/EveryMonthView';
import { TwiceAMonthView } from 'features/budgeting/components/TwiceAMonthView';
import { muteAll, useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { formatISO } from 'date-fns';
import { Frequencies } from 'reducers/types';
import { useAppDispatch, useAppStore } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { getOrdinalSuffix } from 'utils/formatting';
import { deleteAllIncomes, setIncome } from 'actions/payday';
import { budgetingPeriodChanged, fetchAnalyticsBudgetingData, } from 'actions/expenses';
import { useMarginBottom } from 'hooks/useMarginBottom';
import Modal from 'utils/packages/Modal';
import EmptyComponent from 'design-system/EmptyComponent';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { useSetupStepLabel } from 'features/onboarding-report/hooks/useSetupStepLabel';
import { isWeb } from '../../../constants';
import { getMonthToPeriodRatio, incomeToPeriod, toBudgetApiPayload, } from '../utils';
import NthWorkingDayView from '../components/NthWorkingDayView';
export default withAnimatedNavigationHeader(({ navigation, route }) => {
    const { value, label, title, description2 } = route.params.option;
    const colors = useColors();
    const setupStepLabel = useSetupStepLabel(2);
    const styles = useStyles(styleSet);
    const marginBottom = useMarginBottom();
    const store = useAppStore();
    useLayoutEffect(() => {
        navigation.setOptions({
            ...(isWeb ? { headerTitle: EmptyComponent } : {}),
            headerRight: () => (<HelpScreenButton articlesKey={HELP_ARTICLES_KEY.BUDGETING}/>),
        });
    }, [colors.background.light]);
    const { frequency: initFrequency, days: initDays, weekday: initWeekDay, weekNumber: initWeekNumber, ...restPeriod } = route.params.period;
    const [period, setPeriod] = useState({
        frequency: initFrequency || Frequencies.Monthly,
        weekday: initWeekDay || DaysOfTheWeek.Monday,
        weekNumber: initWeekDay === 5 && initDays?.length === 7 ? 5 : initWeekNumber || 1,
        days: initDays || [],
        ...restPeriod,
    });
    const dispatch = useAppDispatch();
    const [, , , fetch] = useFetchOld(muteAll);
    const { onSavePeriod, flow, monthlyBudget, budgetState, hasPrefilledBudgets, createOwnBudget, } = route.params;
    const savePeriod = useCallback(async (period) => {
        if (onSavePeriod) {
            onSavePeriod(period);
        }
        else {
            const previousIncomes = store.getState().user.incomes;
            if (value === Frequencies.Monthly) {
                await fetch(deleteAllIncomes(previousIncomes));
            }
            else {
                await fetch(setIncome(toBudgetApiPayload(period), previousIncomes));
            }
            dispatch(fetchAnalyticsBudgetingData());
            dispatch(budgetingPeriodChanged());
        }
        return false;
    }, [onSavePeriod, value]);
    const [bottomText, setBottomText] = useState();
    const [bottomTextVariableMonth, setBottomTextVariableMonth] = useState({
        on: period.weekNumber === 5
            ? 'Last'
            : period.weekNumber + getOrdinalSuffix(period.weekNumber || 1),
        daysOfTheWeek: DaysOfTheWeek[period.weekday || DaysOfTheWeek.Monday],
    });
    const setDays = useCallback((days) => {
        if (value === Frequencies.FixedDate) {
            setBottomText(<Text TextThin-16 Secondary centered>
              Your period will reset on the{' '}
              <Text Text-16 Secondary>
                {days[0]}
                {getOrdinalSuffix(days[0])} day of each month
              </Text>
            </Text>);
        }
        else if (value === Frequencies.TwiceAMonth) {
            const sortedDays = days.slice().sort((a, b) => a - b);
            setBottomText(<Text TextThin-16 Secondary centered>
              Your period will reset on the{' '}
              <Text Text-16 Secondary>
                {sortedDays[0]}
                {getOrdinalSuffix(sortedDays[0])} and {sortedDays[1]}
                {getOrdinalSuffix(sortedDays[1])} day of each month
              </Text>
            </Text>);
        }
        else if (value === Frequencies.NthLastWorkingDay) {
            setBottomText(<Text TextThin-16 Secondary centered>
              Your period will start on the{' '}
              {days[0] === 2 ? (<Text Text-16 Secondary>
                  penultimate working day
                </Text>) : (<Text Text-16 Secondary>
                  {days[0] === 1
                        ? null
                        : `${days[0]}${getOrdinalSuffix(days[0])} `}
                  last working day
                </Text>)}{' '}
              of the month
            </Text>);
        }
        setPeriod((prevPeriod) => ({ ...prevPeriod, days }));
    }, [value]);
    const setBySetPos = useCallback((bySetPos) => {
        if (value === Frequencies.NthLastWorkingDay && bySetPos) {
            if (bySetPos > 5) {
                setBottomText(<Text TextThin-16 Red centered>
                Enter a number between 1 and 5
              </Text>);
            }
            else {
                setBottomText(<Text TextThin-16 Secondary centered>
                Your period will start on the{' '}
                {bySetPos === 2 ? (<Text Text-16 Secondary>
                    penultimate working day
                  </Text>) : (<Text Text-16 Secondary>
                    {bySetPos === 1
                            ? null
                            : `${bySetPos}${getOrdinalSuffix(bySetPos)} `}
                    last working day
                  </Text>)}{' '}
                of the month
              </Text>);
            }
        }
        setPeriod((prevPeriod) => ({
            ...prevPeriod,
            bySetPos: bySetPos ? -bySetPos : undefined,
        }));
    }, [value]);
    useEffect(() => {
        if (value === Frequencies.NthLastWorkingDay &&
            route.params.period.bySetPos) {
            setBySetPos(Math.abs(route.params.period.bySetPos));
        }
    }, [route.params.period.bySetPos, setBySetPos, value]);
    const setWeekDay = useCallback((weekday) => {
        if (value === Frequencies.EveryMonth ||
            value === Frequencies.EveryFourWeeks ||
            value === Frequencies.EveryTwoWeeks ||
            value === Frequencies.Weekly) {
            setBottomTextVariableMonth((prevState) => ({
                ...prevState,
                daysOfTheWeek: DaysOfTheWeek[weekday],
            }));
        }
        setPeriod((prevPeriod) => ({ ...prevPeriod, weekday }));
    }, [value]);
    const setWeekNumber = useCallback((weekNumber) => {
        if (value === Frequencies.EveryMonth) {
            setBottomTextVariableMonth((prevState) => ({
                ...prevState,
                on: weekNumber === 5
                    ? 'Last'
                    : `${weekNumber}${getOrdinalSuffix(weekNumber || 1)}`,
            }));
        }
        setPeriod((prevPeriod) => ({ ...prevPeriod, weekNumber }));
    }, [value]);
    const cycleContent = useMemo(() => {
        switch (value) {
            case Frequencies.FixedDate:
                return <FixedDateView days={period.days} onChange={setDays}/>;
            case Frequencies.EveryMonth:
                return (<EveryMonthView selectedDay={period.weekday} weekNumber={period.weekNumber || 1} setWeekNumber={setWeekNumber} onSelectDay={setWeekDay}/>);
            case Frequencies.EveryFourWeeks:
                return (<WeeklyView selectedDay={period.weekday} onSelectDay={setWeekDay}/>);
            case Frequencies.EveryTwoWeeks:
                return (<WeeklyView selectedDay={period.weekday} onSelectDay={setWeekDay}/>);
            case Frequencies.Weekly:
                return (<WeeklyView selectedDay={period.weekday} onSelectDay={setWeekDay}/>);
            case Frequencies.TwiceAMonth:
                return <TwiceAMonthView days={period.days} onChange={setDays}/>;
            default:
                return null;
        }
    }, [
        period.days,
        period.weekNumber,
        period.weekday,
        setDays,
        setWeekDay,
        setWeekNumber,
        value,
    ]);
    const onPeriodSave = useCallback((cb, newPeriod) => {
        const currentMonthToPeriodRatio = getMonthToPeriodRatio(toBudgetApiPayload(route.params.currentPeriod));
        const newMonthToPeriodRatio = getMonthToPeriodRatio(toBudgetApiPayload(newPeriod));
        if (currentMonthToPeriodRatio === newMonthToPeriodRatio) {
            cb();
            return;
        }
        Modal.showConfirmation({
            title: 'Change period',
            subtitle: 'You are about to change your period. Would you also like to edit your budget?',
            confirmButtonTitle: 'No, just change period',
            cancelButtonTitle: 'Yes, update budget',
            onConfirm: cb,
            onPressCancel: () => navigation.navigateDeprecated('SetUpBudget', { newPeriod }),
            onBackdropPress: cb,
        });
    }, [route.params.currentPeriod]);
    const onSaveCycle = useCallback(() => {
        Keyboard.dismiss();
        if (value === Frequencies.LastWorkingDay ||
            value === Frequencies.Monthly) {
            savePeriod({ ...period, frequency: value });
            if (hasPrefilledBudgets) {
                navigation.navigateDeprecated('ProcessAutoSetUpBudget', {
                    flow: 'Set',
                    period: { ...period },
                    resetKey: Date.now(),
                });
            }
            else {
                onPeriodSave(() => navigation.goBack(), period);
            }
        }
        else if ([Frequencies.EveryTwoWeeks, Frequencies.EveryFourWeeks].includes(value)) {
            navigation.navigateDeprecated('HolidaySelection', {
                flow: route.params.flow,
                keyBack: route.key,
                period,
                onSavePeriod: (period) => {
                    navigation.navigateDeprecated('SelectDate', {
                        onSubmitDate: (startDate) => {
                            savePeriod({
                                ...period,
                                frequency: value,
                                startDate,
                            });
                            if (flow === 'Onboarding') {
                                navigation.navigateDeprecated('ProcessAutoSetUpBudget', {
                                    flow: 'Onboarding',
                                    period: { ...period, frequency: value, startDate },
                                });
                                return;
                            }
                            if (flow === 'SetAnalytics') {
                                return;
                            }
                            if (flow === 'Set') {
                                if (!budgetState) {
                                    if (createOwnBudget) {
                                        navigation.navigateDeprecated('EditBudget', {
                                            flow,
                                            period: { ...period, frequency: value, startDate },
                                        });
                                    }
                                    else {
                                        navigation.navigateDeprecated('ProcessAutoSetUpBudget', {
                                            flow,
                                            period: { ...period, frequency: value, startDate },
                                        });
                                    }
                                }
                                else {
                                    navigation.navigateDeprecated('Budgets', {
                                        flow,
                                        monthlyBudget,
                                        budgetState,
                                        period: { ...period, frequency: value, startDate },
                                    });
                                }
                            }
                            else if (hasPrefilledBudgets) {
                                navigation.navigateDeprecated('ProcessAutoSetUpBudget', {
                                    flow: 'Set',
                                    period: { ...period, frequency: value, startDate },
                                    resetKey: Date.now(),
                                });
                            }
                            else {
                                onPeriodSave(() => navigation.navigateDeprecated('EditBudget', {
                                    flow,
                                    budgetState,
                                    period: { ...period, frequency: value, startDate },
                                }), { ...period, frequency: value, startDate });
                            }
                        },
                        longTitle: 'When was your last\npayday?',
                        preTitle: flow === 'Onboarding' ? setupStepLabel : undefined,
                        props: {
                            maxDate: formatISO(new Date()),
                        },
                    });
                },
            });
        }
        else {
            const onSavePeriod = (period) => {
                savePeriod({ ...period, frequency: value });
                const formattedPeriod = incomeToPeriod(toBudgetApiPayload({ ...period, frequency: value }));
                const periodToUse = value === Frequencies.NthLastWorkingDay
                    ? formattedPeriod
                    : { ...period, frequency: value };
                if (flow === 'Onboarding') {
                    navigation.navigateDeprecated('ProcessAutoSetUpBudget', {
                        flow: 'Onboarding',
                        period: periodToUse,
                    });
                    return;
                }
                if (flow === 'SetAnalytics') {
                    return;
                }
                if (flow === 'Set') {
                    if (!budgetState) {
                        if (createOwnBudget) {
                            navigation.navigateDeprecated('EditBudget', {
                                flow,
                                period: periodToUse,
                            });
                        }
                        else {
                            navigation.navigateDeprecated('ProcessAutoSetUpBudget', {
                                flow,
                                period: periodToUse,
                            });
                        }
                    }
                    else {
                        navigation.navigateDeprecated('Budgets', {
                            flow,
                            monthlyBudget,
                            budgetState,
                            period: periodToUse,
                        });
                    }
                }
                else if (hasPrefilledBudgets) {
                    navigation.navigateDeprecated('ProcessAutoSetUpBudget', {
                        flow: 'Set',
                        period: periodToUse,
                        resetKey: Date.now(),
                    });
                }
                else {
                    onPeriodSave(() => navigation.navigateDeprecated('EditBudget', {
                        flow,
                        budgetState,
                        period: periodToUse,
                    }), periodToUse);
                }
            };
            const navigateToHolidaySelection = (period) => {
                navigation.navigateDeprecated('HolidaySelection', {
                    flow: route.params.flow,
                    keyBack: route.key,
                    period,
                    onSavePeriod,
                });
            };
            if (value === Frequencies.EveryMonth &&
                period.weekNumber === 5 &&
                period.weekday === 5) {
                navigation.navigateDeprecated('LastFridaySelection', {
                    flow: route.params.flow,
                    keyBack: route.key,
                    period,
                    onSavePeriod: navigateToHolidaySelection,
                    hasPrefilledBudgets,
                });
            }
            else if (value === Frequencies.NthLastWorkingDay) {
                onSavePeriod(period);
            }
            else {
                navigateToHolidaySelection(period);
            }
        }
    }, [
        value,
        savePeriod,
        period,
        hasPrefilledBudgets,
        onPeriodSave,
        route.params.flow,
        route.key,
        flow,
        setupStepLabel,
        budgetState,
        createOwnBudget,
        monthlyBudget,
    ]);
    return (<AppView isInTab={isWeb}>
        <View style={styles.container}>
          <View>
            <StatusBar barStyle="dark-content"/>
            <View style={styles.headerContainer}>
              <Text Text-16 Secondary style={styles.preTitle}>
                {flow === 'Onboarding' ? setupStepLabel : label}
              </Text>
              <Text Text-24 style={styles.title}>
                {title}
              </Text>
              {!!description2 && (<Text TextThin-16 Secondary>
                  {description2}
                </Text>)}
            </View>
            {!!cycleContent && (<View style={[
                styles.cycleContent,
                value !== Frequencies.FixedDate &&
                    value !== Frequencies.TwiceAMonth && {
                    backgroundColor: 'transparent',
                    paddingHorizontal: 0,
                },
            ]}>
                {cycleContent}
              </View>)}
          </View>

          <View style={{
            marginBottom: Number(marginBottom.marginBottom || 0) + HEIGHT + rem(16),
        }}>
            {value === Frequencies.EveryMonth && (<Text TextThin-16 Secondary centered>
                Your period will reset every
                <Text Text-16 Secondary>
                  {' '}
                  {bottomTextVariableMonth.on}{' '}
                  {bottomTextVariableMonth.daysOfTheWeek}{' '}
                </Text>
              </Text>)}

            {value === Frequencies.EveryFourWeeks && (<Text TextThin-16 Secondary centered>
                Your period will reset every
                <Text Text-16 Secondary>
                  {' '}
                  4 weeks{' '}
                </Text>
                on
                <Text Text-16 Secondary>
                  {' '}
                  {bottomTextVariableMonth.daysOfTheWeek}{' '}
                </Text>
              </Text>)}

            {value === Frequencies.EveryTwoWeeks && (<Text TextThin-16 Secondary centered>
                Your period will reset every
                <Text Text-16 Secondary>
                  {' '}
                  2 weeks{' '}
                </Text>
                on
                <Text Text-16 Secondary>
                  {' '}
                  {bottomTextVariableMonth.daysOfTheWeek}{' '}
                </Text>
              </Text>)}

            {value === Frequencies.Weekly && (<Text TextThin-16 Secondary centered>
                Your period will reset every
                <Text Text-16 Secondary>
                  {' '}
                  week{' '}
                </Text>
                on
                <Text Text-16 Secondary>
                  {' '}
                  {bottomTextVariableMonth.daysOfTheWeek}{' '}
                </Text>
              </Text>)}

            {(value === Frequencies.FixedDate ||
            value === Frequencies.TwiceAMonth) &&
            bottomText}
          </View>
        </View>

        {value === Frequencies.NthLastWorkingDay ? (<NthWorkingDayView bottomText={bottomText} onPress={onSaveCycle} value={period.bySetPos ? Math.abs(period.bySetPos).toString() : ''} onChange={(text) => {
                const number = Number(text);
                setBySetPos(number);
                if (Number.isNaN(number) || !number) {
                    setBottomText(undefined);
                }
            }}/>) : (<Button title="Continue" brand floating onPress={onSaveCycle}/>)}
      </AppView>);
}, ({ colors, route }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
    hasCustomTitle: true,
    title: route.params.option?.label,
}));
const styleSet = createStyleSheets((colors) => ({
    container: {
        marginHorizontal: rem(16),
        flex: 1,
        justifyContent: 'space-between',
    },
    headerContainer: {
        paddingBottom: rem(16),
    },
    cycleContent: {
        padding: rem(16),
        backgroundColor: colors.cards.onDark,
        borderRadius: rem(16),
    },
    title: {
        marginBottom: rem(8),
    },
    preTitle: {
        marginBottom: rem(4),
    },
}));
